import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GeneralFormData } from '../general-form-data.model';
import { parseLocalDate } from '../../../utils/date-time-formatter';
import { FormStep, RegulatoryCardBlock, ParagraphBlock  } from '../form-steps.interface';

export class VoluntaryRespiratorUse {
  id?: string;
  submittedByName: string = '';
  submittedByEmail: string = '';
  submissionDate?: Date;

  // ----- Section 1: Employee Information -----
  employeeName: string = '';
  jobTitle: string = '';
  department: string = '';

  // ----- Section 2: Respirator Information -----
  // Values: 'filtering_facepiece' | 'half_face_elastomeric' | 'full_face_elastomeric' | 'other'
  respiratorType: string = '';
  // Is this respirator your personal property?
  isPersonalProperty: string = '';

  // ----- Section 4: Employee Acknowledgment -----
  employeeSignature: Array<string> = [];
  signatureDate: Date | null = null;

  constructor(data?: Partial<VoluntaryRespiratorUse>) {
    if (data) {
      Object.assign(this, {
        ...data,
        submissionDate: data.submissionDate
          ? new Date(data.submissionDate)
          : new Date(),
        signatureDate: data.signatureDate
          ? parseLocalDate(data.signatureDate as any)
          : null,
      });
    }
  }

  static getRespiratorTypeOptions(): string[] {
    return [
      'Filtering facepiece (N95, KN95, disposable dust mask)',
      'Half-Face Elastomeric',
      'Full-Face Elastomeric',
      'Other',
    ];
  }

  static getFormSteps(): FormStep[] {
    return [


      {
        id: 'respirator-details',
        shortTitle: 'Respirator Details',
        title: 'Respirator Details',
        icon: 'shield-checkmark-outline',
        description:
          'Enter employee information and select the type of respirator being worn',
        sections: [
                 {
            title: 'Purpose',
         infoBlocks: [
              {
                type: 'paragraph',
                content:
                  'This form documents that the employee has voluntarily chosen to wear a disposable particulate respirator for dust or nuisance particulates only after the Safety Department has determined it does not create a hazard. Voluntary use of half-mask or full-face air-purifying respirators is prohibited to ensure proper selection and use of respirators in accordance with workplace hazard assessments and the respiratory protection program.',
              },
            ],
            fields: [],
          },
          {
            title: 'Section 1 - Employee Information',

            fields: ['employeeName', 'jobTitle', 'department'],
          },
          {
            title: 'Section 2 - Respirator Information',
            subtitle:
              '(Voluntary-use filtering facepiece respirators—e.g., N95, KN95—do not require a medical evaluation or fit test)',
            fields: ['respiratorType', 'isPersonalProperty'],
          },
        ],
      },
      {
        id: 'acknowledgment',
        shortTitle: 'Acknowledgment',
        title: 'Employee Acknowledgment',
        icon: 'pencil-outline',
        description:
          'Review regulatory information and provide your signature to acknowledge voluntary use',
        sections: [
          {
            title: 'Section 3 - Required Regulatory Information (Appendix D)',
            infoBlocks: [
              {
                type: 'regulatoryCard',
                title: 'The following information is required by safety regulations.',
                subtitle:
                  'By signing, you acknowledge that you have read and understand the following statements.',
                listItems: [
                  {
                    text: 'Respirators can protect you, but only if they are <strong>used properly</strong>.',
                  },
                  {
                    text: 'If you choose to wear a respirator when it is not required, you must still <strong>use it safely</strong>.',
                  },
                  {
                    text: "Always follow the <strong>manufacturer's instructions</strong> for use, cleaning, storage, and maintenance.",
                  },
                  {
                    text: 'Choose respirators <strong>certified by NIOSH</strong>. Do not use products that are not NIOSH-approved.',
                  },
                  {
                    text: 'If the respirator becomes damaged, difficult to breathe through, or contaminated, <strong>leave the area and replace it</strong>.',
                  },
                  {
                    text: 'If you experience dizziness, shortness of breath, or medical symptoms, <strong>report it immediately and discontinue use</strong>.',
                  },
                  {
                    text: 'For elastomeric or full-face respirators:',
                    subItems: [
                      'Your employer must ensure the respirator does not create a hazard.',
                      'You must keep the respirator clean and sanitary.',
                      '<strong>A medical evaluation is required.</strong>',
                    ],
                  },
                ],
              },
            ],
            fields: [], // No form fields, just informational content for the employee to review before signing
          },
          {
            title: 'Section 4 - Employee Acknowledgment',
            fields: ['signatureDate', 'employeeSignature'],
          },
        ],
      },
    ];
  }


  static getRegulatoryCardBlock(): RegulatoryCardBlock | undefined {
  const steps = VoluntaryRespiratorUse.getFormSteps();
  const section = steps[1]?.sections?.[0];
  return section?.infoBlocks?.find(b => b.type === 'regulatoryCard') as RegulatoryCardBlock | undefined;
}

static getPurposeParagraphBlock(): ParagraphBlock | undefined {
  const steps = VoluntaryRespiratorUse.getFormSteps();
  const section = steps[0]?.sections?.[0];
  return section?.infoBlocks?.find(b => b.type === 'paragraph') as ParagraphBlock | undefined;
}

  static getFormInfo(): GeneralFormData {
    return {
      formType: 'voluntary-respirator-use',
      formTitle: 'Voluntary Respirator Use',
      formIcon: 'shield-checkmark-outline',
      cardImage: 'assets/form-card-tiles/voluntary-respirator-use.png',
      formAttachmentRequirements: {
        uploads: [],
        signatures: [
          {
            identifier: 'employeeSignature',
            label: this.getFieldMappings()['employeeSignature'],
            required: true,
          },
        ],
      },
    };
  }

  static getFieldMappings(): Record<string, string> {
    return {
      submittedByName: 'Submitter Name',
      submittedByEmail: 'Submitter Email',
      submissionDate: 'Submission Date',

      // Section 1
      employeeName: 'Employee Name',
      jobTitle: 'Job Title',
      department: 'Department',

      // Section 2
      respiratorType: 'Type of Respirator',
      isPersonalProperty: 'Is This Respirator Your Personal Property?',

      // Section 4
      employeeSignature: 'Employee Signature',
      signatureDate: 'Date',
    };
  }
}
